import { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";

const GrayOverlay = ({ overlayOpacity }) => {
    const map = useMap();
    const overlayRef = useRef(null);
    const mountedRef = useRef(true);

    const redrawOverlay = () => {
        // Guard: check if component is still mounted and map exists
        if (!mountedRef.current || !map) return;
        // Guard: check if map is still valid
        if (!map.getContainer()) return;
        // Guard: check if pane still exists
        const pane = map.getPane("grayOverlay");
        if (!pane) return;

        // Remove old rectangle
        if (overlayRef.current) {
            try {
                map.removeLayer(overlayRef.current);
            } catch (e) {}
            overlayRef.current = null;
        }

        try {
            const bounds = [[-85, -180], [85, 180]];
            const rect = L.rectangle(bounds, {
                pane: "grayOverlay",
                stroke: false,
                color: "transparent",
                weight: 0,
                fill: true,
                fillColor: "#000000",
                fillOpacity: overlayOpacity,
                interactive: false
            });
            rect.addTo(map);
            overlayRef.current = rect;
        } catch (e) {
            // Silently fail if map is being torn down
        }
    };

    useEffect(() => {
        if (!map) return;

        // Create pane
        let pane = map.getPane("grayOverlay");

        if (!pane) {
            pane = map.createPane("grayOverlay");
        }

        // Important
        pane.style.zIndex = "350";
        pane.style.pointerEvents = "none";

        // Initial draw
        redrawOverlay();

        const resizeMap = () => {
            if (!mountedRef.current) return;

            // Give browser time to finish F11 resize
            requestAnimationFrame(() => {
                try {
                    if (map.getContainer()) {
                        map.invalidateSize({
                            animate: false,
                            pan: false
                        });

                        // Redraw after Leaflet updates its dimensions
                        setTimeout(() => {
                            redrawOverlay();
                        }, 100);
                    }
                } catch (e) {
                    // Map may be unmounting
                }
            });
        };

        // Normal browser/window resize
        window.addEventListener("resize", resizeMap);

        // Browser fullscreen API
        document.addEventListener("fullscreenchange", resizeMap);

        // Watch actual map container
        const container = map.getContainer();
        if (!container) return () => {
            window.removeEventListener("resize", resizeMap);
            document.removeEventListener("fullscreenchange", resizeMap);
        };

        let resizeObserver = null;

        if (window.ResizeObserver) {
            resizeObserver = new ResizeObserver(() => {
                if (mountedRef.current) {
                    resizeMap();
                }
            });

            resizeObserver.observe(container);
        }

        return () => {
            window.removeEventListener("resize", resizeMap);
            document.removeEventListener("fullscreenchange", resizeMap);

            if (resizeObserver) {
                resizeObserver.disconnect();
            }

            if (overlayRef.current) {
                try {
                    if (map && map.getContainer()) {
                        map.removeLayer(overlayRef.current);
                    }
                } catch (e) {}
                overlayRef.current = null;
            }
        };
    }, [map]);

    // Update opacity only
    useEffect(() => {
        if (!overlayRef.current || !mountedRef.current) return;

        try {
            overlayRef.current.setStyle({
                fillOpacity: overlayOpacity
            });
        } catch (e) {}
    }, [overlayOpacity]);

    // Track mount/unmount
    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    return null;
};

export default GrayOverlay;